import PropTypes from "prop-types";
import React from "react";

import { SMALL, useDeviceInfo } from "@swa-ui/browser";
import { Card, Heading, Image, PageGrid } from "@swa-ui/core";
import { classNames } from "@swa-ui/string";

import { applyOverrides } from "../applyOverrides";
import { CallToAction } from "../CallToAction";
import { TextContent } from "../TextContent";
import styles from "./BannerWithCardLayout2.module.scss";
import {
  backgroundProperties,
  callToActionPropType,
  cardAlignmentPropType,
  textPropType,
} from "./BannerWithCardProptypes";

export const BannerWithCardLayout2 = (props) => {
  const { background, callToAction, cardAlignment, disclaimer, text } = props;
  const { primaryText, primaryTitle, secondaryTitle } = text ?? {};
  const { align } = applyOverrides(cardAlignment);
  const { isLargeOrXlarge, screenSize } = useDeviceInfo();

  return screenSize === SMALL ? renderContentForSmallScreen() : renderContent();

  function renderContent() {
    return (
      <div className={styles.bannerWithCardContainer} style={{ ...getBannerWithCardStyles() }}>
        {renderGraphicImagePath()}
        <PageGrid className={getGridContainerClass()}>
          <Card className={styles.cardContainer}>{renderCardContent()}</Card>
        </PageGrid>
      </div>
    );
  }

  function renderContentForSmallScreen() {
    return (
      <div className={styles.bannerWithCardContainer}>
        <div className={styles.backgroundContainer} style={{ ...getBackgroundStyles() }} />
        <Card className={styles.cardContainer}>{renderCardContent()}</Card>
      </div>
    );
  }

  function renderGraphicImagePath() {
    const graphicContent = background?.[0] ?? {};
    const { height, imagePath, width } = applyOverrides(graphicContent, screenSize);
    const imageProps = {
      alt: "",
      className: styles.bannerWithCardImage,
      fitToContainer: true,
      height,
      role: "presentation",
      src: imagePath,
      width,
    };

    return (
      <div className={styles.backgroundContainer} style={{ ...getBackgroundStyles() }}>
        {imagePath && <Image {...imageProps} />}
      </div>
    );
  }

  function renderCardContent() {
    return (
      <div className={styles.cardContentContainer}>
        {primaryTitle?.value && <Heading {...getHeadingProps(primaryTitle)} />}
        {secondaryTitle?.value && <Heading {...getHeadingProps(secondaryTitle)} />}
        {primaryText?.value && <TextContent {...primaryText} />}
        {callToAction && <CallToAction {...getCallToActionProps()} />}
        {disclaimer?.value && renderDisclaimer()}
      </div>
    );
  }

  function renderDisclaimer() {
    return (
      <div className={styles.disclaimer}>
        <TextContent {...disclaimer} />
      </div>
    );
  }

  function getBannerWithCardStyles() {
    return {
      ...(align === "right" && isLargeOrXlarge && { justifyItems: "end" }),
    };
  }

  function getBackgroundStyles() {
    const backgroundContent = background?.[0] ?? {};
    const { color, imagePath, type } = applyOverrides(backgroundContent, screenSize);

    return {
      ...(screenSize === SMALL && type === "image" && { backgroundImage: `url(${imagePath})` }),
      ...(type === "color" && { backgroundColor: color }),
      ...(align === "left" && { justifyContent: "flex-end" }),
    };
  }

  function getHeadingProps(headingTitle) {
    return {
      bottomSpacing: "large",
      children: <TextContent {...headingTitle} />,
      headingLevel: 2,
      styleLevel: headingTitle.styleLevel,
    };
  }

  function getCallToActionProps() {
    return {
      className: styles.ctaLink,
      ...(screenSize === SMALL && { additionalProps: { fullWidth: true } }),
      ...callToAction,
    };
  }

  function getGridContainerClass() {
    return classNames({
      [styles.leftAlignmentGridContainer]: align === "left",
      [styles.rightAlignmentGridContainer]: align === "right",
    });
  }
};

BannerWithCardLayout2.displayName = "BannerWithCardLayout2";
BannerWithCardLayout2.propTypes = {
  /** Background image/color content for the banner. */
  background: PropTypes.oneOfType([backgroundProperties, PropTypes.arrayOf(backgroundProperties)]),

  /** Optional object in the card container that contains link/button props. */
  callToAction: callToActionPropType,

  /** Card to be aligned left/right on the background image, and shifts the image accordingly */
  cardAlignment: cardAlignmentPropType,

  /** Optional object in the card container that contains HTML value for a legal disclaimer. */
  disclaimer: PropTypes.shape({
    value: PropTypes.string.isRequired,
  }),

  /** Optional object in the card container for two headings, and text content to be displayed. */
  text: PropTypes.shape({
    primaryText: textPropType,
    primaryTitle: textPropType,
    secondaryTitle: textPropType,
  }),
};
